import { Search, Plus, Filter, Database } from "lucide-react";
import type { MapStyleLibraryToolbarProps } from "../../contracts/map-styles";
import { cn } from "../../lib/utils";
import { Input } from "../../internal/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../internal/primitives/select";
import { MapAssetStatList } from "../map-assets/MapAssetStatList";

export function MapStyleLibraryToolbar({
  title,
  description,
  searchValue,
  searchPlaceholder = "Search styles...",
  scopeValue,
  scopeOptions,
  sourceValue,
  sourcePlaceholder = "All Sources",
  sourceOptions,
  summaryItems = [],
  primaryActionLabel,
  onSearchChange,
  onScopeChange,
  onSourceChange,
  onPrimaryAction,
  className,
}: MapStyleLibraryToolbarProps) {
  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          <h1 className="truncate text-xl font-black tracking-tight text-foreground">
            {title}
          </h1>
          {description && (
            <p className="max-w-2xl text-xs text-muted-foreground/80">
              {description}
            </p>
          )}
        </div>

        {primaryActionLabel && onPrimaryAction && (
          <button
            onClick={onPrimaryAction}
            className="inline-flex h-9 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-xs font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            {primaryActionLabel}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4 border-y border-border/40 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="relative w-full max-w-none sm:max-w-sm">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60" />
            <Input
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-8 text-xs"
            />
          </div>

          <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="flex w-full min-w-0 flex-col gap-1.5 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
              <div className="flex items-center gap-1.5 px-1.5 text-muted-foreground/60">
                <Filter className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">
                  Scope
                </span>
              </div>
              <Select value={scopeValue} onValueChange={onScopeChange as any}>
                <SelectTrigger className="h-9 w-full min-w-0 text-xs sm:w-[130px]">
                  <SelectValue placeholder="All Scopes" />
                </SelectTrigger>
                <SelectContent>
                  {scopeOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      className="text-xs"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {sourceOptions && onSourceChange && (
              <div className="flex w-full min-w-0 flex-col gap-1.5 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
                <div className="flex items-center gap-1.5 px-1.5 text-muted-foreground/60">
                  <Database className="h-3.5 w-3.5" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">
                    Source
                  </span>
                </div>
                <Select value={sourceValue} onValueChange={onSourceChange}>
                  <SelectTrigger className="h-9 w-full min-w-0 text-xs sm:w-[160px]">
                    <SelectValue placeholder={sourcePlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="text-xs"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{option.label}</span>
                          {option.description && (
                            <span className="text-[10px] opacity-60">
                              {option.description}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {summaryItems.length > 0 && (
          <div className="w-full rounded-lg bg-muted/30 px-4 py-2 ring-1 ring-border/40 lg:w-auto lg:shrink-0">
            <MapAssetStatList items={summaryItems} className="justify-between" />
          </div>
        )}
      </div>
    </div>
  );
}
